import React, { useContext, useCallback, useState, SyntheticEvent } from 'react'
import { MediaItemMenu } from 'components/AssetPanels/Media/MediaItemMenu/MediaItemMenu'
import { useSelector } from 'react-redux'
import * as Selectors from 'selectors'
import { selectGpuComputeCap } from 'selectors/app'
import { SOURCE_FILE_TYPES, INSTALL_COMPONENT } from 'enums'
import { useAction, useOnClickWithoutPropagation } from 'hooks'
import * as Actions from 'actions'
import { BaseAssetsListContext } from 'components/AssetPanels/BaseAssetsList'
import { ConfirmationDialog } from 'components/base/ConfirmationDialog/ConfirmationDialog'
import { SelectDialog } from 'components/base/SelectDialog'
import Scrollbars from 'components/base/Scrollbars'
import FoldersTree from 'components/AssetPanels/Media/FoldersTree'
import styles from 'components/AssetPanels/Media/FoldersTree/folderTree.module.scss'
import { TranslationContext } from 'contexts/TranslationContext'
import { MediaAsset, VideoAsset, AudioAsset } from 'models/Asset'
import { addSourceFileByFrameTimeCode } from 'actions/addSourceFileByFrameTimeCode'
import { useAddSourceFileToTimeline } from 'hooks/useAddSourceFileToTimeline'
import { normalizePath } from 'Util'


type Props = {
  anchorEl: React.ReactNode
  asset: MediaAsset,
  isFolder: boolean,
  useRemove: boolean,
}

function MediaContextMenu({ anchorEl, asset, isFolder, useRemove }: Props) {
  // for Scrollbars rerender
  const [ , setForce ] = useState(false)

  const { t } = useContext(TranslationContext)
  const {
    selectedItems,
    folders,
    deletionDialog,
    foldersDialog,
    handleFolderSelect,
    onMoveMediaItemsToFolder,
    selectedFolder,
    onCloseContextMenu: onClose,
    menuPositionCoords,
    clickedMediaElemId,
    isOpenContextMenu,
    isOpenDialog,
  } = useContext(BaseAssetsListContext)


  const isMoveToEnabled = useSelector(state => Selectors.selectAreThereFolders(state,
    { type: SOURCE_FILE_TYPES.MEDIA, selectedItems: folders }))
    && __CFG__.SOURCE_FILES_MANAGEMENT.MOVE_TO_FOLDERS
  const onnxNames = useSelector(state => Selectors.nabletHld.selectHldOnnxNames(state))
  const gpuComputeCap = useSelector(selectGpuComputeCap)
  const isNabletHldEnabled = asset instanceof VideoAsset
    && (onnxNames.length || __APP_PROFILE__ === 'desktop')
  const transcribeModels = useSelector(state => Selectors.transcribe.selectTranscribeModels(state))
  const isTranscribeEnabled = (((asset instanceof VideoAsset) && asset.audioTracks.length)
    || asset instanceof AudioAsset) && (transcribeModels.length || __APP_PROFILE__ === 'desktop')
  const currentFolder = useSelector(state => Selectors.selectCurrentFolderId(state,
    SOURCE_FILE_TYPES.MEDIA))


  const openNabletHldSettings = useAction(Actions.mainView.showNabletHldSettings, true)
  const setNabletHldFile = useAction(Actions.nabletHld.setFile, asset.fileId, asset.name)


  const onClickCopyUrl = (e: SyntheticEvent) => {
    const currentMediaAsset = selectedItems?.find(selectItem => selectItem.id === asset.id)
    if (currentMediaAsset) {
      navigator.clipboard.writeText(currentMediaAsset.originalUrl)
    }
  }

  const onBrowseOutput = useCallback((e: SyntheticEvent) => {
    const currentMediaAsset = selectedItems?.find(selectItem => selectItem.id === asset.id)
    if (currentMediaAsset) {
      // eslint-disable-next-line no-unused-expressions
      nw?.Shell?.showItemInFolder(normalizePath(currentMediaAsset.path))
    }
  }, [ selectedItems, asset ])

  const stopPropagation = useOnClickWithoutPropagation()
  const handleClickMoveFiles = useOnClickWithoutPropagation(() => {
    foldersDialog.open()
  })
  const forceRerender = useCallback(() => setForce(state => !state), [])

  const onAddSourceFileByFrameTimeCode = useAddSourceFileToTimeline(
    addSourceFileByFrameTimeCode, selectedItems.map(item => item.id)
  )

  const onInstallHighlights = useAction(Actions.app.installComponentAction, { component: INSTALL_COMPONENT.HIGHLIGHTS })

  const [ showInstallOnnxConfirm, setShowInstallOnnxConfirm ] = useState(false)
  const [ showGpuNotSupportedConfirm, setShowGpuNotSupportedConfirm ] = useState(false)

  const onConfirmInstallHighlights = useCallback(() => {
    setShowInstallOnnxConfirm(false)
    onInstallHighlights()
  }, [ onInstallHighlights ])

  const onShowNabletHldDetect = useCallback(() => {
    if (onnxNames.length) {
      setNabletHldFile()
      openNabletHldSettings()
    } else if (gpuComputeCap && gpuComputeCap >= 7.5) {
      setShowInstallOnnxConfirm(true)
    } else {
      setShowGpuNotSupportedConfirm(true)
    }
  }, [ setNabletHldFile, openNabletHldSettings, onnxNames, gpuComputeCap ])

  const setTranscribeFile = useAction(Actions.transcribe.setFile, asset.fileId, asset.name)
  const openTranscribeSettings = useAction(Actions.mainView.showTranscribeSettings, true)

  const [ showInstallWhisperConfirm, setShowInstallWhisperConfirm ] = useState(false)

  const onInstallWhisper = useAction(Actions.app.installComponentAction, { component: INSTALL_COMPONENT.WHISPER })

  const onConfirmInstallWhisper = useCallback(() => {
    setShowInstallWhisperConfirm(false)
    onInstallWhisper()
  }, [ onInstallWhisper ])

  const onShowTranscribe = useCallback(() => {
    if (transcribeModels.length) {
      setTranscribeFile()
      openTranscribeSettings()
    } else {
      setShowInstallWhisperConfirm(true)
    }
  }, [ setTranscribeFile, openTranscribeSettings, transcribeModels ])

  return (
    <>
      <MediaItemMenu
        asset={asset}
        anchorEl={anchorEl}
        open={isOpenContextMenu && !isOpenDialog && clickedMediaElemId === asset.id}
        showMoveTo={isMoveToEnabled}
        onClose={onClose}
        showNabletHld={isNabletHldEnabled}
        showTranscribe={isTranscribeEnabled}
        useRemove={useRemove}
        isTopLayer={currentFolder === undefined}
        onClickDeleteMedia={deletionDialog.open}
        onClickMoveFiles={handleClickMoveFiles}
        onClickMoveToMedia={() => onMoveMediaItemsToFolder()}
        onClickCopyUrl={onClickCopyUrl}
        onBrowseOutput={onBrowseOutput}
        onClickNabletHld={onShowNabletHldDetect}
        onClickTranscribe={onShowTranscribe}
        onAddSourceFileByFrameTimeCode={onAddSourceFileByFrameTimeCode}
        menuPositionCoords={menuPositionCoords}
      />
      {deletionDialog.active && clickedMediaElemId === asset.id
        && (
        <ConfirmationDialog
          onClick={stopPropagation}
          onCancel={deletionDialog.cancel}
          onConfirm={deletionDialog.confirm}
          confirmBtnText={t('MEDIA_CONFIRM_BTN_DELETE')}
          closeBtnText={t('MEDIA_CANCEL_BTN_DELETE')}
          description={t('MEDIA_CONFIRM_DESC_ARE_YOU_SURE_TO_REMOVE_SELECTED')}
          buttonConfirmTitle={t('MEDIA_CONFIRM_BTN_DELETE_TOOLTIP')}
          dataLangConfirmId="MEDIA_CONFIRM_BTN_DELETE"
        />
        )}
      {foldersDialog.active && clickedMediaElemId === asset.id
        && (
        <SelectDialog
          title={t('MEDIA_CONFIRM_TITLE_BROWSER_FOR_FOLDER')}
          confirmBtnText={t('MEDIA_CONFIRM_BTN_OK')}
          onCancel={foldersDialog.cancel}
          onClick={stopPropagation}
          onConfirm={() => foldersDialog.confirm()}
          onMouseUp={stopPropagation}
          dataLangConfirmId="MEDIA_CONFIRM_BTN_OK"
          buttonConfirmTitle={t('MEDIA_CONFIRM_BTN_OK_TOOLTIP')}
        >
          <div className={styles.folderTreeContainer}>
            <Scrollbars>
              <FoldersTree
                onInit={forceRerender}
                selectedFolder={selectedFolder}
                onSelect={handleFolderSelect}
                selectedMediaItems={folders}
              />
            </Scrollbars>
          </div>
        </SelectDialog>
        )}
      {showInstallWhisperConfirm && (
        <ConfirmationDialog
          onClick={stopPropagation}
          onCancel={() => setShowInstallWhisperConfirm(false)}
          onConfirm={onConfirmInstallWhisper}
          confirmBtnText={t('COMMON_YES')}
          closeBtnText={t('COMMON_NO')}
          description={t('MESSAGE_BOX_TRANSCRIBE_MODELS_NOT_FOUND_CONFIRMATION')}
        />
      )}
      {showInstallOnnxConfirm && (
        <ConfirmationDialog
          onClick={stopPropagation}
          onCancel={() => setShowInstallOnnxConfirm(false)}
          onConfirm={onConfirmInstallHighlights}
          confirmBtnText={t('COMMON_YES')}
          closeBtnText={t('COMMON_NO')}
          description={t('MESSAGE_BOX_HLD_ONNX_MODELS_NOT_FOUND_CONFIRMATION')}
        />
      )}
      {showGpuNotSupportedConfirm && (
        <ConfirmationDialog
          onClick={stopPropagation}
          onCancel={() => setShowGpuNotSupportedConfirm(false)}
          onConfirm={() => setShowGpuNotSupportedConfirm(false)}
          confirmBtnText={t('COMMON_OK')}
          closeBtnText=""
          description={t('MESSAGE_BOX_HLD_ONNX_MODELS_REQUIRES_GPU')}
        />
      )}
    </>
  )
}

export default MediaContextMenu
